export const LOG_REDACTED = "[REDACTED]"
export const LOG_LIMITS = { depth: 6, nodes: 1000, array: 50, string: 2000 } as const
type LogValue = null | boolean | number | string | LogValue[] | { [key: string]: LogValue }

const sensitiveKey =
  /^(?:.*(?:password|secret|token|credential|authorization|cookie|privatekey|email|phone|phonenumber|fullname|firstname|lastname|address)|(?:user|customer|person|profile|account|contact|display|billing|shipping|brand|company|organization|client|member|owner|employer|recipient)?(?:name|username)|ssn|apikey)$/i
const presenceFlagKey = /^has[a-z0-9]+$/i
const privatePayloadKey =
  /^(?:.*(?:prompts?|scripts?|transcripts?|titles?|descriptions?|summar(?:y|ies)|captions?|quer(?:y|ies)|searchterms?|body|payload|params|urls?|uris?)|text|content|input|inputdata|output|outputdata|request|response|headers|parameters|brandcontext|error|errormessage|errorstack|failurereason)$/i
const privateMetadataKey =
  /^(?:.*(?:filenames?|filepaths?|filekeys?|objectkeys?|storagekeys?|storagepaths?|cachekeys?|rediskeys?|r2keys?|endpoints?)|useragent|ip|ipaddress|clientip|remoteip|forwardedfor|xforwardedfor)$/i
const privateDiagnosticKey = /^(?:.*(?:messages?|stacks?|errordetails|errorcontext)|err)$/i
const contentMetricKey =
  /^.*(?:prompt|script|transcript|title|description|summary|caption|query|searchterm|text|content|body|payload|input|output|request|response)(?:length|count|size|bytes|chars|words|tokens)$/i
const tokenMetricKeys = new Set(["prompt", "completion", "input", "output", "total", "cached"])
const normalizeKey = (key: string) => key.replace(/[^a-z0-9]/gi, "")

/** Heuristics are a backstop, not proof that arbitrary text contains no secrets. */
function sanitizeText(value: string): string {
  if (value.length > LOG_LIMITS.string) return "[String omitted: length limit]"
  return value
    .replace(/\bBearer\s+[^\s,;]+/gi, `Bearer ${LOG_REDACTED}`)
    .replace(/\b(?:access_token|refresh_token|id_token|api_key|client_secret|password)=([^\s&#]+)/gi, LOG_REDACTED)
    .replace(/\bAIza[0-9A-Za-z_-]{20,}\b/g, LOG_REDACTED)
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, LOG_REDACTED)
}

/**
 * Snapshot JSON-like values before buffering. Never invokes getters or toJSON.
 * Limits bound visited nodes and output; JS property enumeration/proxy traps
 * cannot be given a hard CPU bound. Exotic objects and errors fail closed.
 */
export function sanitizeLogValue(input: unknown): LogValue {
  const ancestors = new WeakSet<object>()
  let nodes = 0
  function visit(value: unknown, depth: number, parentKey = ""): LogValue {
    nodes += 1
    if (nodes > LOG_LIMITS.nodes) return "[Node limit]"
    if (value === null || value === undefined) return null
    if (typeof value === "string") return sanitizeText(value)
    if (typeof value === "boolean") return value
    if (typeof value === "number") return Number.isFinite(value) ? value : "[Non-finite number]"
    if (typeof value !== "object") return "[Unsupported value]"
    if (depth >= LOG_LIMITS.depth) return "[Depth limit]"
    if (ancestors.has(value)) return "[Circular]"
    try {
      if (value instanceof Error) return "[Error details omitted]"
      if (value instanceof Date) {
        const time = Date.prototype.getTime.call(value)
        return Number.isFinite(time) ? Date.prototype.toISOString.call(value) : "[Invalid date]"
      }
      const array = Array.isArray(value)
      const prototype = Object.getPrototypeOf(value)
      if (!array && prototype !== Object.prototype && prototype !== null) return "[Non-JSON object omitted]"
      ancestors.add(value)
      const output: { [key: string]: LogValue } = Object.create(null)
      const list: LogValue[] = []
      let count = 0
      for (const key in value) {
        if (!Object.hasOwn(value, key)) continue
        if (nodes >= LOG_LIMITS.nodes || count >= (array ? LOG_LIMITS.array : LOG_LIMITS.nodes)) {
          if (array) list.push("[Truncated]")
          else output._truncated = "[Truncated]"
          break
        }
        // Array non-index properties cannot become JSON payload fields.
        if (array && !/^(0|[1-9]\d*)$/.test(key)) continue
        count += 1
        const descriptor = Object.getOwnPropertyDescriptor(value, key)
        let child: LogValue = "[Accessor omitted]"
        const normalizedKey = normalizeKey(key)
        const presenceFlag =
          descriptor &&
          "value" in descriptor &&
          typeof descriptor.value === "boolean" &&
          presenceFlagKey.test(normalizedKey)
        const finiteMetric =
          descriptor &&
          "value" in descriptor &&
          typeof descriptor.value === "number" &&
          Number.isFinite(descriptor.value)
        const contentMetric = contentMetricKey.test(normalizedKey)
        const tokenMetric = parentKey === "tokens" && tokenMetricKeys.has(normalizedKey) && finiteMetric
        const payloadKey = presenceFlagKey.test(normalizedKey) ? normalizedKey.slice(3) : normalizedKey
        const operationalMessage = depth === 0 && normalizedKey === "message"
        const privateDiagnostic = privateDiagnosticKey.test(payloadKey) && !presenceFlag && !operationalMessage
        const privatePayload =
          (privatePayloadKey.test(payloadKey) || privateMetadataKey.test(payloadKey)) && !presenceFlag && !tokenMetric
        if (
          (sensitiveKey.test(payloadKey) && !presenceFlag) ||
          privatePayload ||
          privateDiagnostic ||
          (contentMetric && !finiteMetric)
        ) {
          nodes += 1
          child = LOG_REDACTED
        } else if (descriptor && "value" in descriptor) {
          child = visit(descriptor.value, depth + 1, normalizedKey)
        }
        if (array) list.push(child)
        else output[sanitizeText(key)] = child
      }
      ancestors.delete(value)
      return array ? list : output
    } catch {
      // Revoked proxies / reflection failures must not escape into raw fallback logs.
      ancestors.delete(value)
      return "[Unreadable object omitted]"
    }
  }
  return visit(input, 0)
}

export function sanitizeLogRecord(input: object): Record<string, unknown> {
  const result = sanitizeLogValue(input)
  return typeof result === "object" && result !== null && !Array.isArray(result) ? result : {}
}
